// The fixed readout row's own logic: which bucket a cursor position (or "now", at rest) describes,
// and the plain values that describe it. Kept separate from ProductivityChart.vue's
// Highcharts wiring so the lookup itself stays a pure function.

import type { ProductivityBucket, ProductivityComparison } from '@/types/SectionLiveKpi';
import type { ProductivityGranularity } from '@/plugins/store/actions/queries/active-groups';
import { bucketTimestamp, DAY_MS, productivityDate } from './productivityFormatting';
import { productivityPlottableValue } from './productivitySeries';
import { comparisonField, projectComparisonTimestamp } from './productivityComparison';
import { METRIC_KEYS, metricField, PRODUCTIVITY_METRICS, type MetricFlags, type MetricKey } from './productivityMetrics';

/**
 * One figure per metric, without the instant they were read at - what the Section's own readout and a
 * comparison reference both carry, and all the readout row's cells and the on-chart cursor labels
 * need of either. Named by metric rather than by bucket field, since that is how everything
 * downstream of the lookup addresses them.
 */
export type MetricFigures = {
    weight: number | null;
    dailyGain: number | null;
    growthIndex: number | null;
    waterActual: number | null;
    waterNorm: number | null;
    deadCount: number | null;
};

/**
 * Which of those figures each metric is stated from. The one mapping between the metric registry's
 * keys and these field names, so a caller walking METRIC_KEYS can read the matching figure without
 * a switch of its own.
 */
export const METRIC_FIGURE_FIELD: Record<MetricKey, keyof MetricFigures> = {
    weight: 'weight',
    dailyGain: 'dailyGain',
    growthIndex: 'growthIndex',
    water: 'waterActual',
    waterNorm: 'waterNorm',
    deadCount: 'deadCount',
};

/**
 * The figures, plus the instant each of the two grains they were read at is keyed by: `timestamp` for
 * the bucket the row is named after, `dayTimestamp` for the day-grained bucket its day-published
 * figures came from. The two are the same instant at day granularity, where every bucket is a day's
 * own; at hourly they differ by up to a day, and `dayTimestamp` is null on a
 * date the response carries no day bucket for.
 *
 * Both are carried because a series is keyed by the same instant its figure was read at - so finding
 * the point a label belongs on is the same question as finding the figure, asked of the chart instead
 * of the buckets. See metricAnchorMs.
 */
export type ProductivityReadout = MetricFigures & { timestamp: number; dayTimestamp: number | null };

/**
 * The same figures, taken from a comparison reference at the same point in the Production Group, plus
 * the reference's own name so the row can say whose figures these are. Every figure is separately
 * nullable: a reference can be missing a metric outright, or simply not have run this far.
 */
export type ProductivityReferenceReadout = MetricFigures & { name: string };

// The item whose own instant is closest to `timestampMs`, or null when none is inside
// `toleranceMs`. A linear scan rather than a sorted-array search: the buckets a request can return
// are capped per granularity (the backend rejects a request that would exceed it), and nothing here
// guarantees the array arrives pre-sorted.
function nearestByInstant<T>(items: readonly T[], timestampMs: number, instantOf: (item: T) => number, toleranceMs: number): T | null {
    let closest: T | null = null;
    let closestDistanceMs = toleranceMs;
    for (const item of items) {
        const distanceMs = Math.abs(instantOf(item) - timestampMs);
        if (distanceMs < closestDistanceMs) {
            closest = item;
            closestDistanceMs = distanceMs;
        }
    }
    return closest;
}

export function nearestBucket(buckets: ProductivityBucket[], timestampMs: number): ProductivityBucket | null {
    return nearestByInstant(buckets, timestampMs, bucketTimestamp, Infinity);
}

// How far a reference bucket may sit from the point being described and still be taken to describe
// it. Unbounded, the nearest match anywhere past the reference's own last day is always that last
// day, and the row would quote a figure for a stretch the reference never ran - which is exactly
// the stretch the chart shades as reference-only, so it is the stretch a reader is most likely to
// be pointing at.
const REFERENCE_MATCH_TOLERANCE_MS = DAY_MS;

/**
 * The reference figures for the same point in the Production Group as `timestampMs`, from the
 * comparison read re-projected onto the displayed Production Group's own start (see
 * projectComparisonTimestamp). Never null while a reference is loaded - a figure the reference has
 * nothing for comes back null instead, so the row keeps its shape as the cursor crosses either edge
 * of the reference rather than growing and shrinking a line under the reader.
 *
 * Which field each metric reads goes through comparisonField, the same test the halo is drawn by: a
 * reference figure is only comparable where it is stated in the unit the primary series is currently
 * drawn in. At Hour, where the water band is metered litres, that means no water reference is quoted
 * rather than a per-pig figure being quoted as though it were litres.
 */
export function referenceReadoutFrom(
    comparison: ProductivityComparison,
    productionGroupStartIso: string,
    timestampMs: number,
    granularity: ProductivityGranularity
): ProductivityReferenceReadout {
    const bucket = nearestByInstant(
        comparison.buckets,
        timestampMs,
        (candidate) => projectComparisonTimestamp(candidate.day_in_period, productionGroupStartIso),
        REFERENCE_MATCH_TOLERANCE_MS
    );
    const figure = (key: MetricKey): number | null => {
        const field = comparisonField(key, granularity);
        return bucket === null || field === null ? null : bucket[field];
    };

    return {
        name: comparison.name,
        weight: figure('weight'),
        // Filtered exactly as the primary row filters its own two derivations: a Growth Index of
        // -201 is not a reading to quote as a reference just because it arrived in the payload.
        dailyGain: productivityPlottableValue('dailyGain', figure('dailyGain')),
        growthIndex: productivityPlottableValue('growthIndex', figure('growthIndex')),
        waterActual: figure('water'),
        waterNorm: figure('waterNorm'),
        deadCount: figure('deadCount'),
    };
}

/**
 * The instant of the most recent bucket carrying a reading the chart is currently drawing, or null
 * when none does.
 *
 * This is what the readout row rests on while the cursor is off the chart, rather than "now". "Now"
 * reads well only for a Section reporting today; a Section between Production Groups, one whose Sensor
 * last reported a fortnight ago, or one being viewed over a historical range would get today's date
 * beside a full row of em-dashes - sixty pixels of chrome above the chart stating nothing until the
 * cursor entered it. Resting on the last real reading means the row always opens on something, and
 * its own date says when that something was, so a stale Section is legible as stale rather than as
 * empty.
 *
 * "The chart is currently drawing" is the whole of it, and it is asked of `drawable` and the
 * registry's own field-per-granularity rather than of a fixed list of fields named here. A fixed list
 * counts metrics the current granularity reads from a different field (`water_litres` is never
 * plotted at Day) and metrics the reader has switched off, so the row opens on a bucket whose only
 * figure is one nothing on screen is showing, and prints a full set of em-dashes anyway. Asked of
 * what is drawable, the row cannot rest anywhere the chart has nothing.
 */
export function latestReadingTimestamp(buckets: ProductivityBucket[], drawable: MetricFlags, granularity: ProductivityGranularity): number | null {
    // Sensor-backed metrics only. A dead count comes from pig movements rather than from telemetry,
    // so the backend returns a row for every calendar day in range whether or not anything was
    // measured - including today - and counting it makes "the latest bucket carrying a reading"
    // always the last day of the range, which is the exact non-answer this exists to avoid.
    const keys = METRIC_KEYS.filter((key) => drawable[key] && PRODUCTIVITY_METRICS[key].sensor !== null);

    let latest: number | null = null;
    for (const bucket of buckets) {
        // Plausibility applied here too: a bucket whose only figure is an implausible one has nothing
        // the chart will draw, so resting on it puts a date beside a row of em-dashes just as surely
        // as resting on an empty one.
        const carriesReading = keys.some((key) => productivityPlottableValue(key, bucket[metricField(key, granularity)]) !== null);
        if (carriesReading) {
            const timestamp = bucketTimestamp(bucket);
            if (latest === null || timestamp > latest) latest = timestamp;
        }
    }
    return latest;
}

/**
 * The bucket the bucket-grained metrics answer from: the nearest one that is actually of the grain
 * the chart was asked for. At day every bucket is, so this is simply the nearest. At hour
 * the response also carries one bucket per day for the day-grained metrics, and since a day bucket
 * and that day's midnight hour land on the same instant, nearest-by-time alone is a coin toss
 * between them, and a cursor at midnight reads no weight and no water whenever it loses.
 *
 * Falls back to the whole set when the range holds no hourly buckets at all, which is a Section with
 * neither Sensor: its dead counts are then the only thing the row can describe, and describing them
 * beats describing nothing.
 */
function nearestBucketAtGrain(
    buckets: ProductivityBucket[],
    timestampMs: number,
    granularity: ProductivityGranularity
): ProductivityBucket | null {
    if (granularity !== 'hour') return nearestBucket(buckets, timestampMs);
    const hourly = buckets.filter((bucket) => bucket.hour !== null);
    return nearestBucket(hourly.length > 0 ? hourly : buckets, timestampMs);
}

/**
 * The bucket a day-grained metric answers from, for a cursor sitting on `bucket`: the day-grained
 * bucket of the same calendar date. At day that is `bucket` itself - what it carries is already the
 * day's own figure - so this only ever looks elsewhere at hourly.
 */
function dayBucketFor(buckets: ProductivityBucket[], bucket: ProductivityBucket): ProductivityBucket | null {
    if (bucket.hour === null) return bucket;
    return buckets.find((candidate) => candidate.hour === null && candidate.date === bucket.date) ?? null;
}

/**
 * What the readout row and the on-chart labels are both describing at `timestampMs`: one figure per
 * metric, each read from the bucket carrying that metric's own grain, plus the instant of the bucket
 * the row is named after.
 *
 * The raw bucket figures, not the chart's rendered ones - water actual is capped at norm for the
 * column-behind-column rendering (see productivitySeries.ts), but the row exists so a
 * deficit or an excess is read as a real number without that rendering trick built in.
 */
export function readoutAt(
    buckets: ProductivityBucket[],
    timestampMs: number,
    granularity: ProductivityGranularity
): ProductivityReadout | null {
    const bucket = nearestBucketAtGrain(buckets, timestampMs, granularity);
    if (!bucket) return null;
    const dayBucket = dayBucketFor(buckets, bucket);
    // Which of the two answers for a given metric, straight off the registry - so a metric that
    // changes grain, or a seventh arriving published per day, needs no edit here.
    const from = (key: MetricKey) => (PRODUCTIVITY_METRICS[key].publishedGrain === 'day' ? dayBucket : bucket);

    return {
        timestamp: bucketTimestamp(bucket),
        dayTimestamp: dayBucket === null ? null : bucketTimestamp(dayBucket),
        weight: bucket.weight_kg,
        // The two day-over-day derivations are filtered exactly as the chart filters them. The water
        // exception below is a *rendering* trick being undone; this is not - a Growth Index of -201
        // is not a reading the row should repeat just because the chart declined to plot it. Showing
        // it here would contradict the gap in the line and the caveat printed under the chart.
        dailyGain: productivityPlottableValue('dailyGain', bucket.daily_gain_g),
        growthIndex: productivityPlottableValue('growthIndex', bucket.growth_index),
        // Whichever unit this granularity states the metered water in - the readout row and the
        // cursor labels carry the matching unit suffix, so only one of the two is ever non-null.
        waterActual: bucket.water_litres ?? bucket.water_per_pig,
        waterNorm: from('waterNorm')?.norm_water_per_pig ?? null,
        deadCount: from('deadCount')?.dead_count ?? null,
    };
}

/**
 * The instant this metric's own series is keyed at, for the point the readout is describing - which is
 * the x a cursor label for it has to be placed on. A day-published metric's series carries one point
 * per day, so at hourly it is keyed at that day's own instant and not at the hovered hour: looked up
 * by the hovered hour instead, the dead count's label appeared for one hour in twenty-four and its
 * bin was next to impossible to hit.
 *
 * Null where the metric has no bucket to be read from at all, which is a day-published metric on a
 * date the response carries no day bucket for.
 */
export function metricAnchorMs(key: MetricKey, readout: ProductivityReadout): number | null {
    return PRODUCTIVITY_METRICS[key].publishedGrain === 'day' ? readout.dayTimestamp : readout.timestamp;
}

// The row's own date: the calendar day a reading fell on. The time of day an hourly reading fell at
// is named in the row's position chip instead (see periodPositionLabel), so the two never repeat
// each other. Built from the same formats the axis and the tooltips use rather than from the app's
// own `long` - which states the month as a number, so a reading here appeared to be dated in a
// different notation from one three pixels away on the axis.
export function formatReadoutTimestamp(timestamp: number): string {
    return productivityDate(timestamp, 'middle');
}
